import { Component, OnDestroy, Signal, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { AuthService, TotpSetup } from '../services/auth.service';
import { SegmentedInputComponent } from "../../shared/segmented-input/segmented-input.component";
import { NetworkStatusBadgeComponent } from "../../shared/network-status-badge/network-status-badge.component";
import { RuntimeService } from '../../shared/services/runtime.service';
import { NetworkStatusService } from '../../shared/services/network-status.service';
import { SyncConflictService } from '../../shared/services/sync-conflict.service';

@Component({
  selector: 'app-autheticate',
  imports: [FormsModule, SegmentedInputComponent, NetworkStatusBadgeComponent],
  templateUrl: './autheticate.component.html',
  styleUrl: './autheticate.component.scss'
})
export class AutheticateComponent implements OnDestroy {

  @ViewChild(SegmentedInputComponent) private otpInput?: SegmentedInputComponent;

  otpCode = '';
  loading = false;
  totpSetup: TotpSetup | null;
  isOnline: Signal<boolean>;
  secretVisible = false;
  secretCopied = false;
  private copiedTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private authService: AuthService,
    private router: Router,
    private spinner: NgxSpinnerService,
    protected runtime: RuntimeService,
    private networkStatus: NetworkStatusService,
    private toastr: ToastrService,
    private conflictService: SyncConflictService
  ) {
    this.totpSetup = this.authService.getTotpSetup();
    this.isOnline = toSignal(this.networkStatus.isOnline$, {
      initialValue: this.networkStatus.isOnline,
    });
  }

  get showQrSetup(): boolean {
    return !!this.totpSetup?.totpSetupRequired && !!this.totpSetup?.qrCodeImageUri;
  }

  get isCodeComplete(): boolean {
    return this.digitsOnly.length === 6;
  }

  get formattedSecret(): string {
    const secret = this.totpSetup?.secret ?? '';
    return secret.replace(/(.{4})/g, '$1 ').trim();
  }

  private get digitsOnly(): string {
    return this.otpCode.replace(/\D/g, '');
  }

  toggleSecret(): void {
    this.secretVisible = !this.secretVisible;
  }

  copySecret(): void {
    const secret = this.totpSetup?.secret;
    if (!secret) return;

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(secret).then(
        () => this.markCopied(),
        () => this.copyWithFallback(secret)
      );
    } else {
      this.copyWithFallback(secret);
    }
  }

  ngOnDestroy(): void {
    if (this.copiedTimeoutId) {
      clearTimeout(this.copiedTimeoutId);
    }
  }

  private copyWithFallback(text: string): void {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    try {
      if (document.execCommand('copy')) {
        this.markCopied();
      }
    } finally {
      document.body.removeChild(textarea);
    }
  }

  private markCopied(): void {
    this.secretCopied = true;
    if (this.copiedTimeoutId) {
      clearTimeout(this.copiedTimeoutId);
    }
    this.copiedTimeoutId = setTimeout(() => {
      this.secretCopied = false;
      this.copiedTimeoutId = null;
    }, 10000);
  }

  onEnterKey(event: Event): void {
    event.preventDefault();
    if (this.showQrSetup && !this.isOnline()) return;
    this.onAuthenticated();
  }

  onAuthenticated() {
    if (!this.isCodeComplete || this.loading) return;

    this.loading = true;
    this.spinner.show();

    this.authService.verifyTotp(this.digitsOnly).subscribe({
      next: (res) => {
        this.loading = false;
        this.spinner.hide();

        // The API returns HTTP 200 even for an invalid/expired code — the real
        // outcome is in data.verified, so a falsy value must not advance the flow.
        if (!res?.data?.verified) {
          this.toastr.error(res?.data?.message || 'Invalid or expired TOTP code', 'Verification failed');
          this.resetOtp();
          return;
        }

        this.authService.clearMfaPending();
        this.authService.clearTotpSetup();
        this.conflictService.clearPending();
        // This component is freshly created on every login (top-level route, not a
        // reusable workspace tab like flight-list), so it reliably (re)starts the
        // periodic licence revalidation check on every login cycle.
        this.authService.startPeriodicRevalidation();
        // Every successful login routes through data sync when online — first-time
        // setup and a returning login both benefit from a refresh. Skip it only when
        // offline, since data sync needs a network connection and can only fail without one.
        if (this.networkStatus.isOnline) {
          this.router.navigate(['/data-sync'], { replaceUrl: true });
        } else {
          this.router.navigate(['/load-planning/flight-list'], { replaceUrl: true });
        }
      },
      error: () => {
        this.loading = false;
        this.spinner.hide();
        this.resetOtp();
      }
    });
  }

  /** Clears the entered code and puts the cursor back in the first box for a fresh attempt */
  private resetOtp(): void {
    this.otpCode = '';
    // Deferred so ngModel has pushed the empty value into the boxes before focusing
    setTimeout(() => this.otpInput?.focusFirst());
  }

  deleteLicence() {
    this.authService.deleteLicence();
  }
}
